import { useEffect, useMemo, useState } from "react";
import { doc, getDoc, Timestamp } from "firebase/firestore";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { db } from "@/firebase";

interface MailThread {
  id: string;
  customerEmail: string;
  subject: string;
  status: "open" | "waiting" | "closed";
}

interface MailMessageDetail {
  id: string;
  mailId: string;
  from: string;
  to: string;
  body?: string;
  bodyPreview?: string;
  createdAt?: Timestamp;
  status?: string;
  attachments?: Array<{
    fileName?: string;
    downloadUrl?: string;
  }>;
}

const formatDateTime = (timestamp?: Timestamp) => {
  if (!timestamp) return "-";
  return timestamp.toDate().toLocaleString("ja-JP", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const statusLabel = (status?: string) => {
  if (status === "waiting") return "返信待ち";
  if (status === "closed") return "完了";
  if (status === "processing") return "処理中";
  if (status === "open") return "対応中";
  return status || "未処理";
};

export default function MailMessageDetailPage() {
  const { mailId, messageId } = useParams<{ mailId: string; messageId: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  const [mail, setMail] = useState<MailThread | null>(null);
  const [message, setMessage] = useState<MailMessageDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!mailId || !messageId) {
      setError("メール情報が取得できません。");
      setLoading(false);
      return;
    }

    let isMounted = true;
    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const mailSnapshot = await getDoc(doc(db, "mail", mailId));
        const messageSnapshot = await getDoc(doc(db, "mail_messages", messageId));
        if (!isMounted) return;

        if (mailSnapshot.exists()) {
          setMail({ id: mailSnapshot.id, ...(mailSnapshot.data() as MailThread) });
        }
        if (messageSnapshot.exists()) {
          setMessage({
            id: messageSnapshot.id,
            ...(messageSnapshot.data() as MailMessageDetail),
          });
        }
      } catch (err) {
        console.error("Failed to load mail message detail", err);
        if (isMounted) {
          setError("メール詳細の取得に失敗しました。");
        }
      } finally {
        if (isMounted) {
          setLoading(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [mailId, messageId]);

  const bodyText = useMemo(() => {
    if (!message) return "";
    return message.body || message.bodyPreview || "";
  }, [message]);

  const status = message?.status || mail?.status;

  if (loading) return <div className="p-4">Loading...</div>;
  if (error) return <div className="p-4 text-red-600">{error}</div>;
  if (!message) return <div className="p-4">メールが見つかりませんでした。</div>;

  const mailRoot = location.pathname.startsWith("/admin") ? "/admin/mail" : "/mail";
  const replyUrl =
    mailId && messageId
      ? `${mailRoot}/new?mailId=${encodeURIComponent(mailId)}&messageId=${encodeURIComponent(messageId)}`
      : `${mailRoot}/new`;

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      <div className="mx-auto max-w-6xl space-y-6">
        <div className="flex items-center justify-between">
          <div className="space-y-1">
            <button
              type="button"
              onClick={() => navigate("/admin/mail")}
              className="text-sm text-blue-600 hover:underline"
            >
              メール一覧
            </button>
            <p className="text-sm text-blue-600">メール一覧 &gt; メール</p>
            <h1 className="text-2xl font-bold text-gray-800">メールの詳細</h1>
          </div>
        </div>

        <div className="flex flex-wrap gap-2">
          <button
            className="rounded border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50"
            onClick={() => navigate(replyUrl)}
          >
            返信する
          </button>
          <button className="rounded border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50">
            処理済みにする
          </button>
          <button className="rounded border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50">
            転送する
          </button>
          <button className="rounded border border-gray-200 bg-white px-3 py-2 text-sm hover:bg-gray-50">
            その他の操作
          </button>
        </div>

        <div className="rounded-lg border bg-white">
          <div className="border-b px-4 py-3 text-sm font-semibold text-gray-700">メール情報</div>
          <div className="grid grid-cols-1 gap-4 p-4 text-sm text-gray-700 md:grid-cols-2">
            <div>
              <div className="text-xs text-gray-500">状況</div>
              <div className="mt-1 inline-flex rounded-full bg-red-100 px-2 py-1 text-xs font-semibold text-red-600">
                {statusLabel(status)}
              </div>
            </div>
            <div>
              <div className="text-xs text-gray-500">担当者</div>
              <div className="mt-1 text-gray-600">(未設定)</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">フォルダ</div>
              <div className="mt-1 text-blue-600">[未分類]</div>
            </div>
            <div>
              <div className="text-xs text-gray-500">顧客情報</div>
              <div className="mt-1 text-gray-600">{mail?.customerEmail || "-"}</div>
            </div>
          </div>
        </div>

        <div className="rounded-lg border bg-white">
          <div className="flex flex-wrap items-center justify-between gap-3 border-b px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="rounded bg-gray-100 px-3 py-1 text-sm font-semibold text-gray-700">
                No. {message.id.slice(0, 6)}
              </span>
              <span className="text-sm text-gray-500">{formatDateTime(message.createdAt)}</span>
            </div>
            <div className="text-xs text-blue-600">HTMLファイルをダウンロードする</div>
          </div>
          <div className="space-y-3 px-4 py-4 text-sm text-gray-700">
            <div>
              <span className="text-xs text-gray-500">件名</span>
              <div className="font-semibold">{mail?.subject || "-"}</div>
            </div>
            <div className="grid gap-2 md:grid-cols-2">
              <div>
                <span className="text-xs text-gray-500">差出人</span>
                <div>{message.from || "-"}</div>
              </div>
              <div>
                <span className="text-xs text-gray-500">宛先</span>
                <div>{message.to || "-"}</div>
              </div>
            </div>
            {message.attachments?.length ? (
              <div>
                <span className="text-xs text-gray-500">添付ファイル</span>
                <ul className="mt-1 space-y-1">
                  {message.attachments.map((attachment, index) => (
                    <li key={`${attachment.fileName || "attachment"}-${index}`}>
                      {attachment.downloadUrl ? (
                        <a
                          className="text-blue-600 underline"
                          href={attachment.downloadUrl}
                          target="_blank"
                          rel="noreferrer"
                          download
                        >
                          {attachment.fileName || "attachment"}
                        </a>
                      ) : (
                        <span>{attachment.fileName || "attachment"}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ) : null}
          </div>
          <div className="border-t px-4 py-4">
            <div className="rounded border border-dashed border-gray-200 bg-gray-50 p-4 text-sm text-gray-700 whitespace-pre-wrap">
              {bodyText || "本文がありません。"}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
